import { useEffect, useMemo, useRef, useState } from 'react'
import {
  HorizontalScrollLeftButton,
  HorizontalScrollRightButton,
} from 'src/consumer/components/CardList/CardListStyle'
import { defaultColor } from 'src/consumer/constants'
import { getImageLink } from 'src/mentor/helpers/utilityFunctions'
import TileV2Header from '../TileV2Header'
import {
  ArticleLayoutCardAnchor,
  ArticleLayoutCardBackground,
  ArticleLayoutCardContainer,
  ArticleLayoutCardDetails,
  ArticleLayoutCardSecondaryText,
  ArticleLayoutCardsContainer,
  ArticleLayoutTitle,
} from './articleLayoutStyles'
import { FEATURED_TAG_MAP } from 'src/mentor/components/ActivityPlanComponent/constants'

export const CardListComponent = ({
  items = [],
  getTitleField,
  backgroundColor,
  onClick,
  getSecondaryText,
  getMainImage,
  getActionButtonLogic,
  ...rest
}) => {
  return items?.map((item, index) => {
    return (
      <ArticleLayoutCardComponent
        key={index}
        getActionButtonLogic={getActionButtonLogic}
        getMainImage={getMainImage}
        getSecondaryText={getSecondaryText}
        getTitleField={getTitleField}
        item={item}
        onClickAction={onClick}
        {...rest}
      />
    )
  })
}

export default function ArticleLayoutCard({
  headingLogicAndData,
  areTileItemsHovered,
  logicAndData,
}) {
  return (
    <>
      <TileV2Header
        logicAndData={{
          ...headingLogicAndData,
          isOnBackground: headingLogicAndData?.isOnBackground,
        }}
      />
      <ArticleLayoutCardCarouselComponent
        areTileItemsHovered={areTileItemsHovered}
        logicAndData={logicAndData}
      />
    </>
  )
}

export function ArticleLayoutCardCarouselComponent({ areTileItemsHovered, logicAndData }) {
  const cardRef = useRef()
  const [leftButtonVisible, setLeftButtonVisible] = useState(false)
  const [rightButtonVisible, setRightButtonVisible] = useState(false)
  const handleScrollLeft = () => {
    if (cardRef.current) {
      const containerWidth = cardRef.current.offsetWidth
      const scrollDistance = containerWidth * 0.75
      cardRef.current.scrollLeft -= scrollDistance // adjust the scroll distance as needed
    }
  }
  const handleScrollRight = () => {
    if (cardRef.current) {
      const containerWidth = cardRef.current.offsetWidth
      const scrollDistance = containerWidth * 0.75
      cardRef.current.scrollLeft += scrollDistance // adjust the scroll distance as needed
    }
  }
  const handleScroll = (event) => {
    const container = event.target
    const scrollPosition = container.scrollLeft
    const maxScrollLeft = container.scrollWidth - container.clientWidth
    const reachedLeftEnd = scrollPosition === 0
    const reachedRightEnd = scrollPosition === maxScrollLeft
    setLeftButtonVisible(!reachedLeftEnd)
    setRightButtonVisible(!reachedRightEnd)
  }
  useEffect(() => {
    const container = cardRef.current
    const scrollPosition = container.scrollLeft
    const maxScrollLeft = container.scrollWidth - container.clientWidth
    const reachedLeftEnd = scrollPosition === 0
    const reachedRightEnd = scrollPosition === maxScrollLeft
    setLeftButtonVisible(!reachedLeftEnd)
    setRightButtonVisible(!reachedRightEnd)
  }, [])
  return (
    <ArticleLayoutCardsContainer ref={cardRef} onScroll={handleScroll}>
      {areTileItemsHovered && leftButtonVisible && (
        <HorizontalScrollLeftButton onClickFunction={handleScrollLeft} />
      )}

      <CardListComponent {...logicAndData} />

      {areTileItemsHovered && rightButtonVisible && (
        <HorizontalScrollRightButton onClickFunction={handleScrollRight} />
      )}
    </ArticleLayoutCardsContainer>
  )
}

export function ArticleLayoutCardComponent({
  item,
  getTitleField,
  getSubtitleField,
  getPersonDetails,
  getSecondaryText,
  getActionButtonLogic,
  getMainImage,
  useMainImageAsCompleteUrl = false,
  layoutCardContainerStyles = {},
  containerMaxWidth = '300px',
  inFlexContainer = true,
  lightMode = false,
  children,
  cardBackgroundStyles = {},
  getTagType,
  idx,
  getTo,
  onClickAction,
}) {
  
  const ComponentToRender = getTo ? ArticleLayoutCardAnchor : ArticleLayoutCardContainer

  const cardOnClick = useMemo(() => {
    if (onClickAction) {
      return () => onClickAction(item)
    }
    if (getActionButtonLogic) {
      const actionLogic = getActionButtonLogic(item)
      if (actionLogic?.onClick) {
        return () => actionLogic.onClick(item)
      }
    }
    return () => {}
  }, [onClickAction, getActionButtonLogic, item])

  const noPointer = useMemo(() => {
    if (!cardOnClick || cardOnClick.toString() === '() => {}') {
      if (getTo) {
        return false
      }
    }
    return true
  }, [getTo, cardOnClick])

  const to = getTo ? getTo(item) : null

  const foundImageLink = useMainImageAsCompleteUrl
    ? getMainImage(item)
    : getImageLink(getMainImage(item))

  const tagType = getTagType ? getTagType(item, idx) : null

  return (
    <ComponentToRender
      nopointer={noPointer}
      to={to}
      bgColor={defaultColor}
      onClick={cardOnClick}
      inFlexContainer={inFlexContainer}
      maxWidth={containerMaxWidth}
      style={(layoutCardContainerStyles, cardBackgroundStyles)}
      className="___article_layout_card_container"
    >
      {getPersonDetails && <div>{getPersonDetails.getPersonDetails.image}</div>}
      <div className="position-relative">
        {tagType === FEATURED_TAG_MAP.trending && (
          <div
            style={{ borderRadius: '0 0 0 8px' }}
            className="position-absolute top-0 end-0 bg-warning p-1"
          >
            Trending
          </div>
        )}
        {tagType === FEATURED_TAG_MAP.popular && (
          <div
            style={{ borderRadius: '0 0 0 8px' }}
            className="position-absolute top-0 end-0 bg-success p-1"
          >
            Popular
          </div>
        )}
        {getMainImage && <ArticleLayoutCardBackground src={foundImageLink} alt='articleFoundImg' />}
      </div>
      <ArticleLayoutCardDetails>
        {getTitleField && (
          <ArticleLayoutTitle lightMode={lightMode}>{getTitleField(item)}</ArticleLayoutTitle>
        )}
        {getSubtitleField && (
          <ArticleLayoutCardSecondaryText lightMode={lightMode}>
            {getSubtitleField(item)}
          </ArticleLayoutCardSecondaryText>
        )}
        <div style={{ flexGrow: 1, display: 'flex', alignItems: 'flex-end' }}>
          {getSecondaryText && (
            <ArticleLayoutCardSecondaryText lightMode={lightMode}>
              {getSecondaryText(item)}
            </ArticleLayoutCardSecondaryText>
          )}
        </div>
        {/* {getActionButtonLogic && (
          <SquareCardActionButton onClick={() => getActionButtonLogic(item).onClick(item)}>
            {getActionButtonLogic(item).text}
          </SquareCardActionButton>
        )} */}
        {children}
      </ArticleLayoutCardDetails>
    </ComponentToRender>
  )
}
